import { z } from 'zod';
import { IsoDateTime, PageQuery, Uuid, page } from './common.js';
/** Module `deliveries` (docs/modules/deliveries.md). Owner: task S26. */
export const ParcelStatus = z.enum(['waiting', 'collected', 'returned', 'cancelled']);
export const Delivery = z.object({
    id: Uuid,
    siteId: Uuid,
    recipientMemberId: Uuid.nullable(),
    recipientName: z.string(),
    carrier: z.string(),
    reference: z.string(),
    parcels: z.number().int(),
    notes: z.string(),
    /** Where it is kept, e.g. "Post room shelf B". */
    location: z.string(),
    photoFileId: Uuid.nullable(),
    status: ParcelStatus,
    loggedAt: IsoDateTime,
    loggedByLabel: z.string(),
    notifiedAt: IsoDateTime.nullable(),
    remindersSent: z.number().int(),
    collectedAt: IsoDateTime.nullable(),
    collectedByName: z.string().nullable(),
    signatureFileId: Uuid.nullable(),
});
export const DeliveryCreate = z.object({
    siteId: Uuid,
    recipientMemberId: Uuid.nullable().default(null),
    /** Free text when the recipient isn't a member. */
    recipientName: z.string().trim().max(120).default(''),
    carrier: z.string().trim().max(80).default(''),
    reference: z.string().trim().max(120).default(''),
    parcels: z.number().int().min(1).max(100).default(1),
    notes: z.string().trim().max(500).default(''),
    location: z.string().trim().max(120).default(''),
    photoUploadId: Uuid.nullable().default(null),
    notify: z.boolean().default(true),
});
/** Only the fields sent change (written out: `.partial()` on fields with defaults would reset the others to their defaults). */
export const DeliveryUpdate = z.object({
    recipientMemberId: Uuid.nullable().optional(),
    recipientName: z.string().trim().max(120).optional(),
    carrier: z.string().trim().max(80).optional(),
    reference: z.string().trim().max(120).optional(),
    parcels: z.number().int().min(1).max(100).optional(),
    notes: z.string().trim().max(500).optional(),
    location: z.string().trim().max(120).optional(),
});
export const DeliveryCollect = z.object({
    collectedByName: z.string().trim().min(1).max(120),
    /** Signature strokes JSON (ADR 0010 format), optional by setting. */
    signature: z.unknown().nullable().default(null),
    occurredAt: IsoDateTime.optional(),
});
export const DeliveryListQuery = PageQuery.extend({ siteId: Uuid.optional(), status: z.enum(['waiting', 'collected', 'returned', 'cancelled', 'all']).default('waiting'), q: z.string().max(100).optional() });
export const DeliveryList = page(Delivery);
/** Kiosk courier flow (optional): the courier picks a recipient and the parcel count. */
export const KioskDeliveryEvent = z.object({
    idempotencyKey: Uuid,
    occurredAt: IsoDateTime,
    recipientMemberId: Uuid.nullable(),
    recipientName: z.string().trim().max(120).default(''),
    carrier: z.string().trim().max(80).default(''),
    parcels: z.number().int().min(1).max(100).default(1),
});
/** Companion: "you have a parcel". */
export const MyDelivery = Delivery.pick({ id: true, siteId: true, carrier: true, parcels: true, location: true, loggedAt: true, status: true }).extend({ siteName: z.string() });
export const MyDeliveries = z.object({ items: z.array(MyDelivery) });
export const DeliveriesSiteSettings = z.object({
    /** Courier button on this site's kiosks. */
    kioskCourierFlow: z.boolean().default(false),
    requireSignatureOnCollect: z.boolean().default(false),
});
export const DeliveriesTenantSettings = z.object({
    /** Remind the recipient after this many hours if not collected; 0 = never. */
    reminderHours: z.number().int().min(0).max(168).default(24),
    maxReminders: z.number().int().min(0).max(10).default(2),
    /** Days to keep delivery records; 0 = keep. */
    retentionDays: z.number().int().min(0).default(365),
});
//# sourceMappingURL=deliveries.js.map